import { keepPreviousData, useQuery } from "@tanstack/preact-query"
import { useMemo } from "react"

import { Drawer, DrawerContent } from "@/components/ui/drawer"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useIsMobile } from "@/hooks/use-mobile"

import type { Log } from "../../../server/types"
import { api } from "../../api"
import { JsonViewBlock } from "./json"
import { extractMessages, MessageList } from "./messages"

type Detail = Log & { request: unknown; response: unknown }

export function LogDetailDialog(props: { id: string | null; onClose: () => void }) {
  const isMobile = useIsMobile()
  const { data: detail } = useQuery<Detail>({
    queryKey: ["log", props.id],
    queryFn: () => api(`/logs/${props.id}`).then((r) => r.json()),
    enabled: !!props.id,
    placeholderData: keepPreviousData,
  })
  const messages = useMemo(
    () => (detail ? extractMessages(detail.request, detail.response, detail.api) : null),
    [detail],
  )

  return (
    <Drawer
      open={!!props.id}
      onOpenChange={(open) => !open && props.onClose()}
      swipeDirection={isMobile ? "down" : "right"}
    >
      <DrawerContent
        style={isMobile ? undefined : { "--drawer-content-width": "36rem" }}
        className="p-4 pb-0"
      >
        <Tabs
          key={messages ? "messages" : "raw"}
          defaultValue={messages ? "messages" : "request"}
          className="min-h-0 flex-1 gap-3"
        >
          <TabsList>
            {messages && <TabsTrigger value="messages">messages</TabsTrigger>}
            <TabsTrigger value="request">request</TabsTrigger>
            <TabsTrigger value="response">response</TabsTrigger>
          </TabsList>
          {messages && (
            <TabsContent value="messages" keepMounted className="min-h-0 overflow-auto">
              <MessageList messages={messages} />
            </TabsContent>
          )}
          {(["request", "response"] as const).map((k) => (
            <TabsContent key={k} value={k} keepMounted className="min-h-0 overflow-auto">
              {detail == null ? (
                "loading..."
              ) : detail[k] == null ? (
                <span className="text-muted-foreground">null</span>
              ) : (
                <JsonViewBlock value={detail[k]} />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </DrawerContent>
    </Drawer>
  )
}
